import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  Cloud,
  Cpu,
  Database,
  FileText,
  Globe2,
  KeyRound,
  Layers,
  MessageSquare,
  Plug,
  Search,
  ShieldCheck,
  Sparkles,
  Table2,
  Timer,
  Workflow,
  Wrench,
  Zap,
} from 'lucide-react';
import type {
  AuthMethod,
  ClientAuthOption,
  ClientType,
  DataModelOperation,
  IdentityProvider,
  LlmRouteTokenClaims,
  McpCapabilityKind,
  McpEnvironment,
  McpRepresentation,
  McpTransport,
  PolicyEffect,
  PublishScope,
  PublishSourceKind,
  RiskLevel,
  SelficCapabilityKind,
  TrustPolicy,
  GatewayTab,
} from './governedMcpTypes';

export const DEFAULT_CAPABILITY_ICON = 'Wrench';

export const CAPABILITY_ICON_OPTIONS: { key: string; label: string; icon: LucideIcon }[] = [
  { key: 'Wrench', label: 'Tool', icon: Wrench },
  { key: 'Database', label: 'Database', icon: Database },
  { key: 'FileText', label: 'Document', icon: FileText },
  { key: 'Search', label: 'Search', icon: Search },
  { key: 'Zap', label: 'Action', icon: Zap },
  { key: 'Timer', label: 'Process', icon: Timer },
  { key: 'Plug', label: 'Connection', icon: Plug },
  { key: 'Workflow', label: 'Workflow', icon: Workflow },
  { key: 'MessageSquare', label: 'Prompt', icon: MessageSquare },
  { key: 'ShieldCheck', label: 'Secure', icon: ShieldCheck },
  { key: 'Globe2', label: 'API', icon: Globe2 },
  { key: 'Layers', label: 'Catalog', icon: Layers },
  { key: 'Sparkles', label: 'AI', icon: Sparkles },
  { key: 'Table2', label: 'Table', icon: Table2 },
  { key: 'Cloud', label: 'Cloud', icon: Cloud },
  { key: 'Cpu', label: 'Compute', icon: Cpu },
  { key: 'KeyRound', label: 'Auth', icon: KeyRound },
  { key: 'Activity', label: 'Activity', icon: Activity },
];

export function resolveCapabilityIconKey(key?: string | null): string {
  if (key && CAPABILITY_ICON_OPTIONS.some((option) => option.key === key)) return key;
  return DEFAULT_CAPABILITY_ICON;
}

export function getCapabilityIcon(key?: string | null): LucideIcon {
  const resolved = resolveCapabilityIconKey(key);
  return CAPABILITY_ICON_OPTIONS.find((option) => option.key === resolved)?.icon ?? Wrench;
}

export const CAPABILITY_ICON_TONES: { light: string; dark: string }[] = [
  { light: 'bg-indigo-50 text-indigo-600', dark: 'bg-indigo-500/15 text-indigo-300' },
  { light: 'bg-sky-50 text-sky-600', dark: 'bg-sky-500/15 text-sky-300' },
  { light: 'bg-violet-50 text-violet-600', dark: 'bg-violet-500/15 text-violet-300' },
  { light: 'bg-amber-50 text-amber-600', dark: 'bg-amber-500/15 text-amber-300' },
  { light: 'bg-emerald-50 text-emerald-600', dark: 'bg-emerald-500/15 text-emerald-300' },
  { light: 'bg-rose-50 text-rose-600', dark: 'bg-rose-500/15 text-rose-300' },
  { light: 'bg-teal-50 text-teal-600', dark: 'bg-teal-500/15 text-teal-300' },
  { light: 'bg-orange-50 text-orange-600', dark: 'bg-orange-500/15 text-orange-300' },
  { light: 'bg-fuchsia-50 text-fuchsia-600', dark: 'bg-fuchsia-500/15 text-fuchsia-300' },
  { light: 'bg-cyan-50 text-cyan-600', dark: 'bg-cyan-500/15 text-cyan-300' },
];

export function getCapabilityIconTone(id?: string | null): { light: string; dark: string } {
  const value = id || DEFAULT_CAPABILITY_ICON;
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return CAPABILITY_ICON_TONES[hash % CAPABILITY_ICON_TONES.length];
}

export const GOVERNED_MCP_GATEWAY_MODULE_ID = '8b4e2c1a-6d79-4f3e-a1b5-9c0d7e4f2a18';
export const GOVERNED_MCP_GATEWAY_DASHBOARD_ID = 'c1a8e4f2-3b6d-4a91-8e07-5d2f9c4b1a70';
export const GOVERNED_MCP_GATEWAY_TOOLS_ID = 'd2b9f5a3-4c7e-4b02-9f18-6e3a0d5c2b81';
export const GOVERNED_MCP_GATEWAY_CONFIGURATION_ID = 'e3c0a6b4-5d8f-4c13-a029-7f4b1e6d3c92';
export const GOVERNED_MCP_GATEWAY_ACTIVITY_ID = 'f4d1b7c5-6e90-4d24-b13a-805c2f7e4da3';
export const GOVERNED_MCP_GATEWAY_API_KEYS_ID = 'a5e2c8d6-7f01-4e35-c24b-916d3a8f5eb4';
export const GOVERNED_MCP_TOOLS_ACCESS_ID = 'b6f3d9e7-8012-4f46-d35c-027e4b9a6fc5';
export const MCP_ACCESS_MANAGER_MODULE_ID = 'h4b7c2d1-8f3a-4e6b-9c21-1a2b3c4d5e6f';
export const MCP_RESOURCES_MODULE_ID = 'c3f4d5e6-7a8b-49c0-1d23-4e5f6a7b8c9d';

export const GOVERNED_MCP_GATEWAY_PAGE_IDS = [
  GOVERNED_MCP_GATEWAY_DASHBOARD_ID,
  GOVERNED_MCP_GATEWAY_TOOLS_ID,
  GOVERNED_MCP_GATEWAY_CONFIGURATION_ID,
  GOVERNED_MCP_GATEWAY_API_KEYS_ID,
] as const;

export const GOVERNED_MCP_GATEWAY_BASE_PATH = '/portal/governed-mcp-gateway';
export const GOVERNED_MCP_GATEWAY_PATHS = {
  root: GOVERNED_MCP_GATEWAY_BASE_PATH,
  dashboard: `${GOVERNED_MCP_GATEWAY_BASE_PATH}/dashboard`,
  tools: `${GOVERNED_MCP_GATEWAY_BASE_PATH}/tools`,
  configuration: `${GOVERNED_MCP_GATEWAY_BASE_PATH}/configuration`,
  aiUsage: `${GOVERNED_MCP_GATEWAY_BASE_PATH}/ai-usage`,
} as const;

export const GATEWAY_HEADER_EYEBROW = 'AI Gateway Fabric';
export const GOVERNANCE_STATEMENT = 'AI clients request. Selfic decides and governs execution.';

const GATEWAY_TAB_TITLES: Record<Exclude<GatewayTab, 'activity'>, string> = {
  dashboard: 'Dashboard',
  tools: 'Tools',
  configuration: 'Configuration',
  'api-keys': 'AI Usage & Spendings',
};

const GATEWAY_TAB_SUBTITLES: Record<Exclude<GatewayTab, 'activity'>, string> = {
  dashboard: 'Usage, estimated tokens, access coverage, and integrations across the governed MCP host.',
  tools: 'Publish and govern tools, resources, and prompts on the MCP gateway.',
  configuration: 'Host setup, AI client registrations, and access policies.',
  'api-keys': 'API keys, quotas, and spend across gateway usage.',
};

export function gatewayTabFromPath(pathname: string): GatewayTab {
  if (pathname.startsWith(GOVERNED_MCP_GATEWAY_PATHS.tools)) return 'tools';
  if (pathname.startsWith(GOVERNED_MCP_GATEWAY_PATHS.configuration)) return 'configuration';
  if (pathname.startsWith(GOVERNED_MCP_GATEWAY_PATHS.aiUsage)) return 'api-keys';
  return 'dashboard';
}

export function gatewayPathForTab(tab: GatewayTab): string {
  if (tab === 'tools') return GOVERNED_MCP_GATEWAY_PATHS.tools;
  if (tab === 'configuration') return GOVERNED_MCP_GATEWAY_PATHS.configuration;
  if (tab === 'api-keys') return GOVERNED_MCP_GATEWAY_PATHS.aiUsage;
  return GOVERNED_MCP_GATEWAY_PATHS.dashboard;
}

export function gatewayTitleForTab(tab: GatewayTab): string {
  if (tab === 'activity') return GATEWAY_TAB_TITLES.dashboard;
  return GATEWAY_TAB_TITLES[tab];
}

export function gatewaySubtitleForTab(tab: GatewayTab): string {
  if (tab === 'activity') return GATEWAY_TAB_SUBTITLES.dashboard;
  return GATEWAY_TAB_SUBTITLES[tab];
}

export const TENANT_SLUG = 'acme-financial';
export const TENANT_NAME = 'Acme Financial';
export const TENANT_ID = '6a6eee6f-75d7-4479-892d-80b5f225e004';
export const API_KEYS_STORAGE_KEY = 'selfic.governed-mcp-gateway.api-keys.v2';
export const QUOTA_USERS_STORAGE_KEY = 'selfic.governed-mcp-gateway.quota-users.v3';
export const MCP_JWT_DEFAULT_TTL_SECONDS = 60 * 60 * 24 * 30;
export const MCP_JWT_EXPIRY_OPTIONS: { value: number; label: string }[] = [
  { value: 60 * 60, label: '1 hour' },
  { value: 60 * 60 * 8, label: '8 hours' },
  { value: 60 * 60 * 24, label: '1 day' },
  { value: 60 * 60 * 24 * 7, label: '7 days' },
  { value: MCP_JWT_DEFAULT_TTL_SECONDS, label: '30 days' },
  { value: 60 * 60 * 24 * 90, label: '90 days' },
  { value: 60 * 60 * 24 * 365, label: '1 year' },
];
export const MCP_HOST = 'https://mcp.selficai.com';
export const MCP_PUBLIC_URL = `${MCP_HOST}/mcp`;
export const MCP_BASE_URL = MCP_HOST;
export const SINGLETON_ENDPOINT_ID = 'ep-selfic';
export const SINGLETON_ENDPOINT_SLUG = 'mcp';

export const STORAGE_KEY = 'selfic.governed-mcp-gateway.v10';

export function buildMcpUrl(_slug?: string): string {
  return MCP_PUBLIC_URL;
}

export function normalizeMcpToolName(value: string): string {
  return value.replace(/\s+/g, '_');
}

const ASSET_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ASSET_ID_LEN = 36;

export function isCatalogAssetId(value: string): boolean {
  return ASSET_ID_RE.test((value || '').trim());
}

function splitPrefixedAssetName(value: string): { assetId: string; slug: string } | null {
  const text = (value || '').trim();
  if (text.length <= ASSET_ID_LEN + 1) return null;
  const assetId = text.slice(0, ASSET_ID_LEN);
  const separator = text.charAt(ASSET_ID_LEN);
  if ((separator === '_' || separator === ':') && isCatalogAssetId(assetId)) {
    return { assetId, slug: normalizeMcpToolName(text.slice(ASSET_ID_LEN + 1)) };
  }
  return null;
}

export function splitAssetToolName(value: string): { assetId: string; slug: string } {
  return splitPrefixedAssetName(value) || { assetId: '', slug: normalizeMcpToolName((value || '').trim()) };
}

export function displayMcpToolName(value: string): string {
  return splitAssetToolName(value).slug || (value || '').trim();
}

export function composeAssetToolName(assetId: string, slug: string): string {
  const id = (assetId || '').trim();
  const name = normalizeMcpToolName((slug || '').trim());
  if (isCatalogAssetId(id) && name) return `${id}_${name}`;
  return name;
}

export function buildPublishedAssetBinding(
  slug: string,
  assetId: string,
  extras?: { assetKind?: string; assetName?: string },
): {
  mcpToolName: string;
  selectedTableIds: string[];
  assetKind: string;
  assetName: string;
  publishScope: PublishScope;
} {
  const id = (assetId || '').trim();
  return {
    selectedTableIds: id ? [id] : [],
    mcpToolName: composeAssetToolName(id, slug),
    assetKind: extras?.assetKind || '',
    assetName: extras?.assetName || '',
    publishScope: 'asset',
  };
}

export function buildPublishedModelBinding(
  slug: string,
  selectedTableIds: string[],
): {
  mcpToolName: string;
  selectedTableIds: string[];
  assetKind: string;
  assetName: string;
  publishScope: PublishScope;
} {
  return {
    mcpToolName: normalizeMcpToolName((slug || '').trim()),
    selectedTableIds: selectedTableIds.map((id) => (id || '').trim()).filter(Boolean),
    assetKind: '',
    assetName: '',
    publishScope: 'data_model',
  };
}

export function isLegacyAssetPublishedTool(item?: {
  publishScope?: string | null;
  mcpToolName?: string | null;
  assetKind?: string | null;
} | null): boolean {
  if (!item) return false;
  if (item.publishScope === 'data_model') return false;
  if (item.publishScope === 'asset') return true;
  return Boolean(splitAssetToolName(item.mcpToolName || '').assetId || (item.assetKind || '').trim());
}

export function isQueryablePublishAsset(asset?: { assetKind?: string | null } | null): boolean {
  const kind = (asset?.assetKind || 'table').trim().toLowerCase();
  return kind !== 'file' && kind !== 'folder';
}

export function publishedToolNameTaken(
  name: string,
  published: { id: string; mcpToolName: string }[],
  excludeId?: string,
): boolean {
  const normalized = normalizeMcpToolName((name || '').trim()).toLowerCase();
  if (!normalized) return false;
  return published.some((item) => {
    if (excludeId && item.id === excludeId) return false;
    return normalizeMcpToolName(item.mcpToolName || '').toLowerCase() === normalized;
  });
}

export function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

export const ENVIRONMENT_OPTIONS: { value: McpEnvironment; label: string }[] = [
  { value: 'development', label: 'Development' },
  { value: 'test', label: 'Test' },
  { value: 'production', label: 'Production' },
];

export const TRANSPORT_OPTIONS: { value: McpTransport; label: string }[] = [
  { value: 'streamable-http', label: 'Streamable HTTP' },
  { value: 'sse', label: 'SSE (compatibility)' },
];

export const AUTH_METHOD_OPTIONS: { value: AuthMethod; label: string; comingSoon?: boolean }[] = [
  { value: 'universal_token', label: 'Universal Token' },
  { value: 'oauth_oidc', label: 'OAuth / OIDC', comingSoon: true },
  { value: 'api_key', label: 'API key (non-user agents)', comingSoon: true },
  { value: 'mtls', label: 'Mutual TLS', comingSoon: true },
];

export const IDP_OPTIONS: { value: IdentityProvider; label: string; comingSoon?: boolean }[] = [
  { value: 'okta', label: 'Okta', comingSoon: true },
  { value: 'azure_ad', label: 'Microsoft Entra ID', comingSoon: true },
  { value: 'ping', label: 'PingFederate', comingSoon: true },
  { value: 'auth0', label: 'Auth0', comingSoon: true },
  { value: 'selfic_sso', label: 'Selfic SSO', comingSoon: true },
];

export const CLIENT_TYPE_OPTIONS: { value: ClientType; label: string; hint: string }[] = [
  { value: 'claude_enterprise', label: 'Claude Enterprise', hint: 'Anthropic Claude connected via MCP' },
  { value: 'chatgpt_enterprise', label: 'ChatGPT Enterprise', hint: 'OpenAI ChatGPT with MCP tools' },
  { value: 'microsoft_copilot', label: 'Microsoft Copilot', hint: 'Copilot Studio / M365 Copilot' },
  { value: 'internal_agent', label: 'Internal enterprise agent', hint: 'Selfic-hosted or custom internal agent' },
  { value: 'custom', label: 'Custom client', hint: 'Any application that calls Selfic, with an optional LLM gateway route' },
];

export const CLIENT_REGISTRATION_PREFIX: Record<ClientType, string> = {
  claude_enterprise: 'claude',
  chatgpt_enterprise: 'chatgpt',
  microsoft_copilot: 'microsoftcopilot',
  internal_agent: 'internalagent',
  custom: 'custom',
};

export const CLIENT_AUTH_OPTIONS: { value: ClientAuthOption; label: string }[] = [
  { value: '', label: 'Not specified' },
  { value: 'oauth', label: 'OAuth' },
  { value: 'api_key', label: 'API key' },
];

export function clientAuthLabel(value: ClientAuthOption | undefined): string {
  if (value === 'oauth') return 'OAuth';
  if (value === 'api_key') return 'API key';
  return '';
}

export function isGeneratedRegistrationId(value: string): boolean {
  return Object.values(CLIENT_REGISTRATION_PREFIX).some((prefix) => new RegExp(`^${prefix}_\\d{6}$`).test(value));
}

export function generateRegistrationId(type: ClientType, taken: Iterable<string> = []): string {
  const prefix = CLIENT_REGISTRATION_PREFIX[type];
  const occupied = new Set(taken);
  for (let attempt = 0; attempt < 40; attempt += 1) {
    const n = Math.floor(100000 + Math.random() * 900000);
    const next = `${prefix}_${n}`;
    if (!occupied.has(next)) return next;
  }
  return `${prefix}_${Date.now().toString().slice(-6)}`;
}

export const TRUST_POLICY_OPTIONS: { value: TrustPolicy; label: string; hint: string }[] = [
  { value: 'enterprise_managed', label: 'Enterprise managed', hint: 'All authenticated enterprise users mapped through Selfic IAM' },
  { value: 'named_users_groups', label: 'Named users / groups', hint: 'Restrict to explicitly listed directory identities' },
  { value: 'service_identity', label: 'Service identity', hint: 'Non-human workload identity; no user impersonation' },
  { value: 'custom', label: 'Custom trust policy', hint: 'Combine directory mapping with additional conditions' },
];

export const MCP_CAPABILITY_OPTIONS: { value: McpCapabilityKind; label: string; hint: string; disabled?: boolean }[] = [
  { value: 'tool', label: 'Tool', hint: 'Parameterized invoke with input and output schemas' },
  { value: 'resource', label: 'Resource', hint: 'URI-addressable records or files' },
  { value: 'prompt', label: 'Prompt', hint: 'Coming soon', disabled: true },
];

export const TOOL_SOURCE_OPTIONS: { value: PublishSourceKind; label: string; hint: string }[] = [
  { value: 'data_model', label: 'Data model', hint: 'Governed records from a connected store' },
  { value: 'workflow', label: 'Workflow', hint: 'Action or long-running process' },
  { value: 'knowledge_base', label: 'Knowledge base', hint: 'Retrieval over indexed documents' },
  { value: 'external_mcp', label: 'External MCP', hint: 'Re-publish a connected MCP tool' },
];

export const RESOURCE_SOURCE_OPTIONS: { value: PublishSourceKind; label: string; hint: string }[] = [
  { value: 'data_model', label: 'Data model', hint: 'Expose selected tables or files as a resource' },
  { value: 'custom_text', label: 'Custom text', hint: 'Describe the resource contract directly' },
];

export const DATA_MODEL_OPERATION_OPTIONS: { value: DataModelOperation; label: string; hint: string }[] = [
  { value: 'query', label: 'Query', hint: 'Ad-hoc or filtered retrieval' },
  { value: 'read', label: 'Read', hint: 'Fetch one record or file by id' },
  { value: 'create', label: 'Create', hint: 'Insert a record or upload a file' },
  { value: 'update', label: 'Update', hint: 'Patch an existing record or file' },
  { value: 'delete', label: 'Delete', hint: 'Remove a record or file' },
];

export const CAPABILITY_KIND_OPTIONS: {
  value: SelficCapabilityKind;
  label: string;
  backedBy: string;
  mcp: string;
}[] = [
  { value: 'information', label: 'Information', backedBy: 'Data Model', mcp: 'Resource and/or Tool' },
  { value: 'search', label: 'Search', backedBy: 'Data Model', mcp: 'Tool' },
  { value: 'action', label: 'Action', backedBy: 'Workflow', mcp: 'Tool' },
  { value: 'process', label: 'Process', backedBy: 'Long-running workflow', mcp: 'Tool + Task' },
  { value: 'external_mcp', label: 'External MCP', backedBy: 'External MCP connection', mcp: 'Tool and/or Resource' },
];

export const REPRESENTATION_LABEL: Record<McpRepresentation, string> = {
  resource: 'Resource',
  tool: 'Tool',
  task: 'Task',
};

export const EFFECT_OPTIONS: { value: PolicyEffect; label: string }[] = [
  { value: 'allow', label: 'Allow' },
  { value: 'deny', label: 'Deny' },
  { value: 'require_approval', label: 'Require approval' },
];

export const RISK_OPTIONS: { value: RiskLevel; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

export const SETUP_STEPS = [
  { id: 'endpoint', label: 'Connect MCP URL', tab: 'configuration' as const, section: 'setup' as const },
  { id: 'client', label: 'Register Client', tab: 'configuration' as const, section: 'clients' as const },
  { id: 'policy', label: 'Configure Access Policy', tab: 'configuration' as const, section: 'policies' as const },
  { id: 'publish', label: 'Publish tools', tab: 'tools' as const, section: null },
  { id: 'test', label: 'Test', tab: 'configuration' as const, section: 'setup' as const },
];

export const TRACE_PIPELINE = [
  'AI Request',
  'Authentication',
  'User Resolution',
  'Client Validation',
  'Capability Discovery/Invocation',
  'Policy Evaluation',
  'Authorization',
  'Execution',
  'Output Governance',
  'MCP Response',
] as const;

export function environmentLabel(value: McpEnvironment): string {
  return ENVIRONMENT_OPTIONS.find((item) => item.value === value)?.label ?? value;
}

export function authMethodLabel(value: AuthMethod): string {
  return AUTH_METHOD_OPTIONS.find((item) => item.value === value)?.label ?? value;
}

export function idpLabel(value: IdentityProvider): string {
  return IDP_OPTIONS.find((item) => item.value === value)?.label ?? value;
}

export function compactAuthLabel(method: AuthMethod, provider?: IdentityProvider): string {
  if (method === 'universal_token') return 'Universal Token';
  if (method === 'oauth_oidc') {
    const idp = provider === 'azure_ad' ? 'Entra ID' : provider ? idpLabel(provider) : 'OIDC';
    return `OAuth 2.1 (${idp})`;
  }
  if (method === 'api_key') return 'API key';
  return 'mTLS';
}

export function clientTypeLabel(value: ClientType): string {
  return CLIENT_TYPE_OPTIONS.find((item) => item.value === value)?.label ?? value;
}

export const LLM_ROUTE_TOKEN_USE = 'llm_gateway_route';
export const LLM_GATEWAY_HOST = 'https://gateway.selfic.ai';

export const LLM_ROUTE_CLAIM_HELP: { key: keyof LlmRouteTokenClaims; label: string; meaning: string }[] = [
  { key: 'tenant_id', label: 'tenant_id', meaning: 'Workspace that owns this client and the selected gateway. Every request stays inside this tenant.' },
  { key: 'sub', label: 'sub', meaning: 'Client identifier bound to this registration. The gateway treats this as the calling principal.' },
  { key: 'registration_id', label: 'registration_id', meaning: 'Portal registration id for this custom client.' },
  { key: 'llm_gateway_id', label: 'llm_gateway_id', meaning: 'The tenant LLM gateway this token is allowed to call.' },
  { key: 'gateway_alias', label: 'gateway_alias', meaning: 'Public model alias applications send as the model name.' },
  { key: 'token_use', label: 'token_use', meaning: 'Marks this JWT as an LLM gateway route token, not a portal or MCP user token.' },
  { key: 'iss', label: 'iss', meaning: 'Issuer that signed the token.' },
  { key: 'aud', label: 'aud', meaning: 'Audience the gateway accepts for this route.' },
  { key: 'iat', label: 'iat', meaning: 'Time the token was issued.' },
  { key: 'exp', label: 'exp', meaning: 'Time the token expires. Rotate before this moment.' },
];

export function llmRouteBaseUrl(gatewayId: string): string {
  return `${LLM_GATEWAY_HOST}/gw/${gatewayId}/v1`;
}

export function llmInferenceBaseUrl(routeKey: string): string {
  return `${LLM_GATEWAY_HOST}/v1/llm/${routeKey}`;
}

export function llmInferenceCurlSample(input: {
  baseUrl: string;
  token: string;
  model?: string;
  kind?: 'chat' | 'embedding';
}): string {
  const origin = (input.baseUrl || '').replace(/\/$/, '');
  if (input.kind === 'embedding') {
    return [
      `curl ${origin}/embeddings \\`,
      `  -H "Authorization: Bearer ${input.token}" \\`,
      `  -H "Content-Type: application/json" \\`,
      `  -d '{"input":"hello world"}'`,
    ].join('\n');
  }
  return [
    `curl ${origin}/chat/completions \\`,
    `  -H "Authorization: Bearer ${input.token}" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -d '{"messages":[{"role":"user","content":"Hello"}],"temperature":0.7,"max_tokens":256,"stream":false}'`,
  ].join('\n');
}

export function llmRouteParameterizedUrl(gatewayId: string, tenantId: string, clientId: string): string {
  const params = new URLSearchParams();
  if (tenantId) params.set('tenant_id', tenantId);
  if (clientId) params.set('client_id', clientId);
  const query = params.toString();
  return query ? `${llmRouteBaseUrl(gatewayId)}?${query}` : llmRouteBaseUrl(gatewayId);
}

export function maskSecret(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (trimmed.length <= 12) return '••••••••';
  return `${trimmed.slice(0, 8)}…${trimmed.slice(-4)}`;
}

export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function claimsFromRouteToken(token: string): LlmRouteTokenClaims | null {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  const tenantId = String(payload.tenant_id || '');
  const sub = String(payload.sub || '');
  const registrationId = String(payload.registration_id || '');
  const gatewayId = String(payload.llm_gateway_id || '');
  if (!tenantId || !sub || !gatewayId) return null;
  return {
    tenant_id: tenantId,
    sub,
    registration_id: registrationId,
    llm_gateway_id: gatewayId,
    gateway_alias: String(payload.gateway_alias || ''),
    token_use: String(payload.token_use || LLM_ROUTE_TOKEN_USE),
    iss: String(payload.iss || ''),
    aud: String(payload.aud || ''),
    iat: Number(payload.iat) || 0,
    exp: Number(payload.exp) || 0,
  };
}

export function capabilityKindLabel(value: SelficCapabilityKind): string {
  return CAPABILITY_KIND_OPTIONS.find((item) => item.value === value)?.label ?? value;
}

export function defaultRepresentations(kind: SelficCapabilityKind): McpRepresentation[] {
  switch (kind) {
    case 'information':
      return ['resource', 'tool'];
    case 'search':
      return ['tool'];
    case 'action':
      return ['tool'];
    case 'process':
      return ['tool', 'task'];
    case 'external_mcp':
      return ['tool'];
    default:
      return ['tool'];
  }
}

export function backedByForKind(kind: SelficCapabilityKind): 'data_model' | 'workflow' | 'long_running_workflow' | 'external_mcp' {
  switch (kind) {
    case 'information':
    case 'search':
      return 'data_model';
    case 'action':
      return 'workflow';
    case 'process':
      return 'long_running_workflow';
    default:
      return 'external_mcp';
  }
}
